import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './AlertDetail.css';
import config from '../config';

// Mini Chart Component
const MiniChart = ({ data, width = 180, height = 40 }) => {
  // Validate data array
  if (!data || data.length === 0) {
    return (
      <div style={{ width, height, overflow: 'hidden' }}>
        <svg width={width} height={height} className="chart">
          <path d={`M 0 ${height} L ${width} ${height}`} stroke="#7f1d1d" strokeWidth="1.5" fill="none" />
        </svg>
      </div>
    );
  }

  // Handle single data point
  if (data.length === 1) {
    const y = height / 2;
    const pathData = `M 0 ${y} L ${width} ${y}`;
    return (
      <div style={{ width, height, overflow: 'hidden' }}>
        <svg width={width} height={height} className="chart">
          <path d={pathData} stroke="#7f1d1d" strokeWidth="1.5" fill="none" />
        </svg>
      </div>
    );
  }

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * (width - 5);
    const y = height - ((value - min) / range) * (height - 5);
    return { x, y };
  });

  const pathData = points.map((point, index) =>
    index === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`
  ).join(' ');

  const areaPath = `${pathData} L ${width - 5} ${height} L 0 ${height} Z`;

  return (
    <div style={{ width, height, overflow: 'hidden' }}>
      <svg width={width} height={height} className="chart">
        <defs>
          <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#dc2626" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#fecdd3" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#gradient)" />
        <path d={pathData} stroke="#7f1d1d" strokeWidth="1.5" fill="none" />
      </svg>
    </div>
  );
};

// Sensor Card Component
const SensorCard = ({ label, value, chartData, showChart = true, batteryLevel = 0 }) => (
  <div className="sensor-card">
    <div className="sensor-label">{label}</div>
    {value && value.trim() !== '' ? (
      <div className="sensor-value">{value}</div>
    ) : null}
    {showChart ? (
      <div className="chart-container">
        <MiniChart data={chartData} width={180} height={40} />
      </div>
    ) : (
      <div className="battery-icon-container">
        <div className="battery-icon">
          <div className="battery-body">
            <div className="battery-fill" style={{ width: `${Math.min(batteryLevel, 100)}%`, backgroundColor: batteryLevel >= 101 ? '#10b981' : undefined }} />
          </div>
          <div className="battery-tip" />
        </div>
      </div>
    )}
  </div>
);

const AlertDetail = () => {
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const [latestData, setLatestData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [catalogue, setCatalogue] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [latestRes, historyRes, catRes] = await Promise.all([
          fetch(`${config.API_BASE_URL}/devices/${deviceId}/latest`, {
            headers: { }
          }),
          fetch(`${config.API_BASE_URL}/devices/${deviceId}/readings?limit=20`, {
            headers: { }
          }),
          fetch(`${config.API_BASE_URL}/sensor-types`, {
            headers: { }
          })
        ]);

        const latest = await latestRes.json();
        const hist = await historyRes.json();
        const cat = await catRes.json();

        setLatestData(latest);
        setHistory(hist.reverse()); // Show oldest to newest for chart
        setCatalogue(cat);
      } catch (error) {
        console.error('Error fetching device details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000); // Poll every 5s
    return () => clearInterval(interval);
  }, [deviceId]);

  const getChartData = (field) => {
    return history.map(h => h[field] || 0);
  };

  const sensorData = [
    {
      id: 'smoke',
      label: 'Smoke',
      value: latestData?.smoke > 100 ? 'DETECTED' : 'CLEAR',
      chartData: getChartData('smoke'),
      showChart: true,
    },
    {
      id: 'co',
      label: 'CO',
      value: latestData?.carbon_monoxide > 10 ? 'DETECTED' : 'CLEAR',
      chartData: getChartData('carbon_monoxide'),
      showChart: true,
    },
    {
      id: 'temperature',
      label: 'Temperature',
      value: `${latestData?.temperature?.toFixed(1) || '--'} °C`,
      chartData: getChartData('temperature'),
      showChart: true,
    },
    {
      id: 'humidity',
      label: 'Humidity',
      value: `${latestData?.humidity || '--'} %`,
      chartData: getChartData('humidity'),
      showChart: true,
    },
    {
      id: 'pressure',
      label: 'Pressure',
      value: `${latestData?.pressure?.toFixed(1) || '--'} hPa`,
      chartData: getChartData('pressure'),
      showChart: true,
    },
    {
      id: 'aqi',
      label: 'Air Quality Index',
      value: `${latestData?.aqi?.toFixed(1) || '--'}`,
      chartData: getChartData('aqi'),
      showChart: true,
    },
    {
      id: 'noise',
      label: 'Noise',
      value: `${latestData?.noise_level?.toFixed(1) || '--'} dBA`,
      chartData: getChartData('noise_level'),
      showChart: true,
    },
    {
      id: 'battery',
      label: 'Battery',
      value: latestData?.battery_level >= 101 ? 'Charging' : `${latestData?.battery_level || '--'}%`,
      chartData: [],
      showChart: false,
      batteryLevel: latestData?.battery_level || 0
    },
    {
      id: 'motion',
      label: 'Motion Presence',
      value: latestData?.motion_presence === 1 ? 'DETECTION' : 'NONE',
      chartData: getChartData('motion_presence'),
      showChart: true,
    },
    {
      id: 'noise_p',
      label: 'Noise Presence',
      value: latestData?.noise_presence === 1 ? 'DETECTION' : 'NONE',
      chartData: getChartData('noise_presence'),
      showChart: true,
    },
    {
      id: 'sd_status',
      label: 'SD Card',
      value: latestData?.sd_detect === 1 ? 'VALID SD' : 'NO SD',
      chartData: getChartData('sd_detect'),
      showChart: true,
    },
    {
      id: 'sd_space',
      label: 'SD Space (Used/Left)',
      value: `${latestData?.sd_space_used || 0}/${latestData?.sd_space_left || 0} Blocks`,
      chartData: getChartData('sd_space_used'),
      showChart: true,
    },
    {
      id: 'controls',
      label: 'Horn/Test',
      value: `${latestData?.horn_hush === 1 ? 'HUSH' : 'OFF'} / ${latestData?.test === 1 ? 'TEST' : 'OFF'}`,
      chartData: [],
      showChart: false,
    },
  ];

  const isConnected = latestData ? (Date.now() - new Date(latestData.received_at + 'Z').getTime()) < 30000 : false;

  return (
    <div className="detail-safe-area">
      <div className="detail-container">
        <div className="detail-content-container">
          {/* Header */}
          <div className="detail-header">
            {/* Back Button */}
            <button
              className="back-button"
              onClick={() => navigate('/')}
            >
              <span className="back-icon">‹</span>
            </button>

            <div className="logo-row">
              {/* Logo */}
              <div className="detail-logo-container">
                <img
                  src="/assets/GlobalAlertzIconA.png"
                  alt="Global Alertz Logo"
                  className="detail-logo-image"
                />
              </div>

              {/* Title */}
              <div className="detail-title-container">
                <div className="detail-title-main">GLOBAL ALERTZ</div>
                <div className="detail-title-sub">MASTER MONITOR</div>
              </div>
            </div>
          </div>

          {/* Device Name and Status */}
          <div className="device-header">
            <div className="detail-device-name">{deviceId ? deviceId.replace(/^MM-/, '') : ''}</div>
            <div className="detail-status-container">
              <div
                className="detail-status-dot"
                style={{ backgroundColor: isConnected ? '#10b981' : '#ef4444' }}
              />
              <div className="detail-status-text">{isConnected ? 'Connected' : 'Disconnected'}</div>
            </div>
          </div>

          {/* Sensor Grid */}
          <div className="sensor-grid">
            {loading ? (
              <div style={{ color: 'white', textAlign: 'center', gridColumn: 'span 2' }}>Loading sensor data...</div>
            ) : (
              sensorData.map((sensor) => (
                <div key={sensor.id} className="grid-item">
                  <SensorCard
                    label={sensor.label}
                    value={sensor.value}
                    chartData={sensor.chartData}
                    showChart={sensor.showChart}
                    batteryLevel={sensor.batteryLevel}
                  />
                </div>
              ))
            )}
          </div>

          {/* Diagnostic Footer */}
          {!loading && latestData && (
            <div className="diagnostic-footer">
              <div className="error-code-label">System Error Code</div>
              <div className="error-code-value">
                0x{(latestData.system_error_code || 0).toString(16).toUpperCase().padStart(4, '0')}
              </div>
            </div>
          )}

          {/* Expandable External Sensors Section */}
          {latestData?.sensors && latestData.sensors.length > 0 && (
            <div className={`external-sensors-expandable ${isExpanded ? 'active' : ''}`}>
              <button 
                className="expand-toggle-button"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <div className="expand-toggle-inner">
                  <span className="expand-toggle-text">🛰️ OTHER SENSORS</span>
                  <span className="expand-toggle-icon">{isExpanded ? '▼' : '▲'}</span>
                </div>
              </button>
              
              {isExpanded && (
                <div className="external-sensor-grid-mini">
                  {latestData.sensors.map((sensor, sIdx) => {
                    const typeInfo = catalogue.find(t => t.typeId === sensor.typeId);
                    const sensorName = sensor.name || typeInfo?.name || `Sensor ${sensor.sensorId}`;
                    
                    return sensor.values.map((val, vIdx) => {
                      const format = typeInfo?.valueFormats[vIdx];
                      const label = format ? `${sensorName} - ${format.label}` : `${sensorName} [${vIdx}]`;
                      const unit = format?.unit || '';
                      
                      const externalChartData = history.map(h => {
                        const s = h.sensors?.find(ext => ext.sensorId === sensor.sensorId);
                        return s?.values[vIdx] || 0;
                      });

                      return (
                        <div key={`${sIdx}-${vIdx}`} className="grid-item">
                          <SensorCard
                            label={label}
                            value={`${typeof val === 'number' ? val.toFixed(1) : val} ${unit}`}
                            chartData={externalChartData}
                            showChart={true}
                          />
                        </div>
                      );
                    });
                  })}
                </div>
              )}
            </div>
          )}

          {/* Linking Button */}
          <button
            className="linking-button"
            onClick={() => navigate(`/linking/${deviceId}`)}
          >
            <span className="linking-text">Linking</span>
          </button>

          {/* Bottom Safe Area Spacer */}
          <div className="detail-bottom-spacer" />
        </div>
      </div>
    </div>
  );
};

export default AlertDetail;

